<!-- FieldTeamsPanel.vue - Panel de gestión de equipos -->
<template>
  <div class="field-teams-panel">
    <!-- Resumen de equipos -->
    <v-row class="mb-4">
      <v-col cols="6">
        <v-card class="text-center pa-2" color="success" variant="outlined">
          <v-card-text>
            <v-icon size="24" class="mb-1">mdi-account-check</v-icon>
            <div class="text-h6">{{ availableTeams.length }}</div>
            <div class="text-caption">Disponibles</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6">
        <v-card class="text-center pa-2" color="info" variant="outlined">
          <v-card-text>
            <v-icon size="24" class="mb-1">mdi-account-clock</v-icon>
            <div class="text-h6">{{ busyTeams.length }}</div>
            <div class="text-caption">Ocupados</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Lista de equipos -->
    <div class="teams-list">
      <v-card
        v-for="team in teams"
        :key="team.id"
        class="mb-3"
        :class="getTeamClass(team)"
        variant="outlined"
      >
        <v-card-title class="d-flex align-center">
          <v-avatar :color="team.statusColor" size="32" class="mr-3">
            <v-icon color="white">mdi-account-group</v-icon>
          </v-avatar>
          {{ team.name }}
          <v-spacer></v-spacer>
          <v-chip
            :color="team.statusColor"
            size="small"
            variant="flat"
          >
            {{ getTeamStatusText(team.status) }}
          </v-chip>
        </v-card-title>
        
        <v-card-text>
          <!-- Líder del equipo -->
          <div class="mb-2">
            <v-chip
              size="small"
              prepend-icon="mdi-account-star"
              variant="outlined"
              color="primary"
            >
              {{ team.leader }}
            </v-chip>
          </div>

          <!-- Miembros del equipo -->
          <div class="mb-3">
            <div class="text-caption text-medium-emphasis mb-1">
              Miembros ({{ team.members.length }})
            </div>
            <div class="members-grid">
              <v-chip
                v-for="member in team.members"
                :key="member.id"
                size="small"
                :color="getMemberStatusColor(member.status)"
                class="mr-1 mb-1"
              >
                <v-icon
                  start
                  size="16"
                  :icon="getMemberStatusIcon(member.status)"
                ></v-icon>
                {{ member.name }}
              </v-chip>
            </div>
          </div>

          <!-- Especialidades -->
          <div class="mb-3" v-if="team.specialties?.length">
            <div class="text-caption text-medium-emphasis mb-1">
              Especialidades
            </div>
            <div>
              <v-chip
                v-for="specialty in team.specialties"
                :key="specialty"
                size="small"
                variant="outlined"
                class="mr-1 mb-1"
              >
                {{ specialty }}
              </v-chip>
            </div>
          </div>

          <!-- Equipamiento -->
          <div class="mb-3" v-if="team.equipment?.length">
            <div class="text-caption text-medium-emphasis mb-1">
              Equipamiento
            </div>
            <div>
              <v-chip
                v-for="equipment in team.equipment"
                :key="equipment.id"
                size="small"
                :color="getEquipmentStatusColor(equipment.status)"
                class="mr-1 mb-1"
              >
                <v-icon
                  start
                  size="16"
                  :icon="getEquipmentIcon(equipment.type)"
                ></v-icon>
                {{ equipment.name }}
              </v-chip>
            </div>
          </div>

          <!-- Ubicación actual -->
          <div v-if="team.location">
            <div class="text-caption text-medium-emphasis">Ubicación:</div>
            <div class="text-body-2">{{ team.location.address }}</div>
          </div>

          <!-- Nivel de disponibilidad -->
          <div class="mt-3">
            <div class="d-flex justify-space-between mb-1">
              <span class="text-caption">Disponibilidad</span>
              <span class="text-caption">{{ team.availabilityLevel }}%</span>
            </div>
            <v-progress-linear
              :model-value="team.availabilityLevel"
              :color="getAvailabilityColor(team.availabilityLevel)"
              height="6"
              rounded
            ></v-progress-linear>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-btn
            v-if="team.status === 'available'"
            @click="$emit('assign-team', team.id)"
            color="primary"
            size="small"
            prepend-icon="mdi-account-arrow-right"
          >
            Asignar
          </v-btn>
          
          <v-btn
            v-if="team.status === 'assigned'"
            @click="$emit('reassign-team', team.id)"
            color="info"
            size="small"
            prepend-icon="mdi-account-switch"
          >
            Reasignar
          </v-btn>
          
          <v-btn
            @click="viewTeamDetails(team)"
            color="secondary"
            size="small"
            prepend-icon="mdi-eye"
          >
            Detalles
          </v-btn>

          <v-spacer></v-spacer>
          
          <v-btn
            icon="mdi-phone"
            size="small"
            variant="text"
            @click="contactTeam(team)"
          ></v-btn>
          
          <v-btn
            icon="mdi-map-marker"
            size="small"
            variant="text"
            @click="showTeamLocation(team)"
          ></v-btn>
        </v-card-actions>
      </v-card>
    </div>

    <!-- Botón de apoyo de emergencia -->
    <v-btn
      block
      color="error"
      prepend-icon="mdi-alert"
      class="mt-4"
      @click="$emit('emergency-support')"
    >
      Solicitar Apoyo de Emergencia
    </v-btn>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  teams: {
    type: Array,
    default: () => []
  },
  assignments: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits([
  'assign-team',
  'reassign-team',
  'emergency-support'
])

// Computed properties
const availableTeams = computed(() => 
  props.teams.filter(team => team.status === 'available')
)

const busyTeams = computed(() => 
  props.teams.filter(team => ['assigned', 'busy'].includes(team.status))
)

// Métodos
const getTeamStatusText = (status) => {
  const texts = {
    available: 'Disponible',
    assigned: 'Asignado',
    busy: 'Ocupado',
    off_duty: 'Fuera de servicio'
  }
  return texts[status] || status
}

const getMemberStatusColor = (status) => {
  const colors = {
    active: 'success',
    break: 'warning',
    unavailable: 'error'
  }
  return colors[status] || 'grey'
}

const getMemberStatusIcon = (status) => {
  const icons = {
    active: 'mdi-check-circle',
    break: 'mdi-pause-circle',
    unavailable: 'mdi-close-circle'
  }
  return icons[status] || 'mdi-help-circle'
}

const getEquipmentStatusColor = (status) => {
  const colors = {
    available: 'success',
    in_use: 'info',
    maintenance: 'warning',
    damaged: 'error'
  }
  return colors[status] || 'grey'
}

const getEquipmentIcon = (type) => {
  const icons = {
    'Instrumento': 'mdi-gauge',
    'Herramientas': 'mdi-wrench',
    'Vehículo': 'mdi-truck',
    'Comunicación': 'mdi-radio'
  }
  return icons[type] || 'mdi-tools'
}

const getAvailabilityColor = (level) => {
  if (level >= 80) return 'success'
  if (level >= 60) return 'warning'
  return 'error'
}

const getTeamClass = (team) => {
  const classes = ['team-card']
  
  if (team.status === 'available') {
    classes.push('team-available')
  } else if (team.status === 'busy') {
    classes.push('team-busy')
  }
  
  return classes.join(' ')
}

const viewTeamDetails = (team) => {
  console.log('👥 Ver detalles del equipo:', team.name)
  // Implementar vista detallada
}

const contactTeam = (team) => {
  console.log('📞 Contactar equipo:', team.name)
  // Implementar contacto
}

const showTeamLocation = (team) => {
  console.log('📍 Mostrar ubicación del equipo:', team.location)
  // Implementar mostrar ubicación
}
</script>

<style scoped>
.field-teams-panel {
  height: 100%;
}

.teams-list {
  max-height: 500px;
  overflow-y: auto;
}

.team-card {
  transition: all 0.3s ease;
}

.team-available {
  background: rgba(76, 175, 80, 0.05);
  border-left: 3px solid #4caf50;
}

.team-busy {
  background: rgba(255, 152, 0, 0.05);
  border-left: 3px solid #ff9800;
}

.team-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.members-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
</style>
